import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import {
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useRoleStore } from "@/store/useRoleStore";
import {
  toEnglishNumber,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ParizCaptcha, { type CaptchaSolution } from "@/modules/ParizCaptcha";

const CAPTCHA_APP_NAME = "userpanel";

const formSchema = z.object({
  nID: z.string().min(1, "نام کاربری الزامی است"),
  pass: z.string().min(1, "رمز عبور الزامی است"),
});

type FormData = z.infer<typeof formSchema>;

const FormLogin = ({
  setIsRegister,
  setIsForgetPassword,
}: {
  setIsRegister: Dispatch<SetStateAction<boolean>>;
  setIsForgetPassword: Dispatch<SetStateAction<boolean>>;
}) => {
  const [serverError, setServerError] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>("user");
  const [showPassword, setShowPassword] = useState(false);
  const [valueNidNumber, setValueNidNumber] = useState("");

  // ── captcha ─────────────────────────────────────────────
  const [captchaSolution, setCaptchaSolution] = useState<CaptchaSolution | null>(null);
  const [captchaError, setCaptchaError] = useState("");

  const navigate = useNavigate();
  const base64CredentialsUser = btoa(`${process.env.REACT_APP_USER_NAME}:${process.env.REACT_APP_USER_PASS}`);
  const base64CredentialsPazirande = btoa(`${process.env.REACT_APP_PAZIRANDE_NAME}:${process.env.REACT_APP_PAZIRANDE_PASS}`);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });
  const setRole = useRoleStore((state) => state.setRole);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const { mutate: fetchLogin, isPending: isPendingLogin } = useMutation({
    mutationFn: async (form: any) => {
      const credentials = selectedOption === "user"
        ? base64CredentialsUser
        : base64CredentialsPazirande;
      const authHeader = `Basic ${credentials}`;

      const response = await axios.post(`${baseURL}/sso/api/v1/login`, form, {
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
    onSuccess: (data: any) => {
      const userId = data?.data?.userId;
      const apps = data?.data?.apps;
      const token = `Bearer ${data?.data?.Access_Token}`;
      const tokenbest = data?.data?.token_best_unify;
      const refreshToken = data?.data?.Refresh_Token;

      localStorage.setItem("userPhone", data?.data?.mobile);
      localStorage.setItem("ID", userId);
      localStorage.setItem("newrole", apps);
      localStorage.setItem("roleEnter", selectedOption);
      localStorage.setItem("token", token);
      localStorage.setItem("tokenbest", tokenbest);
      localStorage.setItem("refreshToken", refreshToken);
      navigate("/");
      window.location.reload();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const msg = error?.response?.data?.message || "خطا در ورود به سیستم";
      toast.error(msg);
      // Captcha token is single-use — any rejection needs a fresh one.
      setCaptchaSolution(null);
      setCaptchaError(msg);
    },
  });

  const onSubmit = async (data: FormData) => {
    if (!captchaSolution) {
      toast.error("لطفاً کپچا را تکمیل کنید");
      return;
    }
    const requestBody = {
      nID: valueNidNumber,
      pass: toEnglishNumber(data?.pass),
      captcha_answer: captchaSolution.answer,
      captcha_payload: captchaSolution.payload,
      captcha_app_name: CAPTCHA_APP_NAME, // "userpanel"
    };
    fetchLogin(requestBody);
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const submitDisabled = isPendingLogin || !captchaSolution;

  return (
<form
      className="w-full flex flex-col justify-center"
      onSubmit={handleSubmit(onSubmit)}
      style={{ width: "100%" }}
    >
      <StackKit
        justifyContent={"center"}
        alignItems={"center"}
        gap={2}
        sx={{ width: { xs: "100%", sm: "100%", md: "80%" } }}
      >
        <img className="w-[250px]" src={"./saee-header-login.svg"} alt={"saee-header-login"} />
        <TypoGraphyKit variant="subtitle1">
          به سامانه پرداختی ساعی خوش آمدید{" "}
        </TypoGraphyKit>
        <TextField
          {...register("nID")}
          placeholder="نام کاربری"
          sx={{
            borderRadius: "10px",
            width: "70%",
          }}
          value={valueNidNumber ? toPersianNumber(valueNidNumber) : ""}
          onChange={(e) => {
            const englishNumber = toEnglishNumber(e.target.value);
            const rawValue = englishNumber.replace(/[^0-9]/g, "");
            if (selectedOption === "user" && rawValue.length <= 10) {
              setValueNidNumber(rawValue);
            } else if (selectedOption === "merchant") {
              setValueNidNumber(rawValue);
            }
          }}
        />
        {errors.nID && (
          <StackKit direction={"row"}>
            <TypoGraphyKit variant="body1" sx={{ color: "red" }}>
              {errors.nID?.message}
            </TypoGraphyKit>
          </StackKit>
        )}

        {errors.nID && (
          <StackKit direction={"row"}>
            <TypoGraphyKit variant="body1" sx={{ color: "red" }}>
              {errors.nID?.message}
            </TypoGraphyKit>
          </StackKit>
        )}

        <TextField
          {...register("pass")}
          type={showPassword ? "text" : "password"}
          placeholder="رمز عبور"
          sx={{ background: "linear-gradient(90deg, #444444 -35.13%, #000000 126.71%)", borderRadius: "10px", width: "70%", border: "none", }}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {errors.pass && (
          <StackKit direction={"row"}>
            <TypoGraphyKit variant="body1" sx={{ color: "red" }}>
              {errors.pass?.message}
            </TypoGraphyKit>
          </StackKit>
        )}

        <RadioGroup value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)} sx={{ gap: 2, width: "100%", display: "flex", justifyContent: "center", alignItems: "center", }}>
          <StackKit direction={"row"} justifyContent={"space-between"} gap={2} sx={{ width: "auto", display: "flex", flexDirection: { xs: 'column', sm: 'column', md: 'row' } }}>
            <FormControlLabel
              value="user"
              control={<Radio sx={{ color: "white" }} />}
              label={
                <TypoGraphyKit sx={{ color: "white" }}>
                  ورود به پنل کاربر
                </TypoGraphyKit>
              }
            />
            <FormControlLabel
              value="merchant"
              control={<Radio sx={{ color: "white" }} />}
              label={
                <TypoGraphyKit sx={{ color: "white" }}>
                  ورود به پنل پذیرنده
                </TypoGraphyKit>
              }
            />
          </StackKit>
        </RadioGroup>

        {/* ── CAPTCHA ─────────────────────────────────────── */}
        <StackKit sx={{ width: "70%" }}>
          <ParizCaptcha
            onSolved={(s) => { setCaptchaSolution(s); setCaptchaError(""); }}
            onChange={(s) => {
              // Keep the solution always in sync with what's typed.
              // If the input is empty / invalid, the parent has no solution.
              setCaptchaSolution(s);
            }}
            error={captchaError}
          />
          {!captchaSolution && (
            <TypoGraphyKit variant="body2" sx={{ fontSize: 12, color: "#aaa", mt: 0.5 }}>
              برای فعال شدن دکمه ورود، کد تصویر را وارد و تأیید کنید
            </TypoGraphyKit>
          )}
        </StackKit>

        {serverError && (
          <TypoGraphyKit sx={{ color: "red", fontSize: "0.9rem" }}>
            {serverError}
          </TypoGraphyKit>
        )}

        <StackKit sx={{ width: "100%" }} alignItems={"center"}>
          <CustomButton
            type="submit"
            disabled={submitDisabled}
            sx={{
              background: submitDisabled
                ? "gray"
                : "linear-gradient(90deg, #F4D7D3 21.62%, #78B9C1 87.47%) !important",
              width: "60%",
            }}
          >
            {isPendingLogin ? (
              <CircularProgress sx={{ color: "#fff" }} size={24} />
            ) : (
              <TypoGraphyKit variant="body1">ورود</TypoGraphyKit>
            )}
          </CustomButton>
          <StackKit alignItems={"start"} sx={{ width: "60%", paddingTop: "0" }}>
            <div style={{ background: "none", border: "none", color: "#3687FF", cursor: "pointer", padding: 0, textDecoration: "none", }} onClick={() => setIsForgetPassword(true)}>
              فراموشی رمز عبور
            </div>
          </StackKit>
        </StackKit>

        <StackKit alignItems={"center"} sx={{ width: "40%" }}>
          <TypoGraphyKit sx={{ background: "none", border: "none", color: "#3687FF", cursor: "pointer", padding: 0, textDecoration: "none", }} onClick={() => setIsRegister(true)}>
            حساب کاربری ندارید؟ ثبت نام کنید
          </TypoGraphyKit>
        </StackKit>
      </StackKit>
    </form>
  );
};

export default FormLogin;
